import { useGlobalNotifier, useMessage } from "@vrf/fr-vrf-core-react";
import {
  FrVrfButton,
  FrVrfFooter,
  FrVrfInputDatepickerField,
  FrVrfRadioButtonField,
  FrVrfSingleLineDisplayField,
  FrVrfTimeInputField,
} from "@vrf/fr-vrf-web-components-react";
import React, { useEffect } from "react";
import { createPath, useNavigate } from "react-router-dom";
import { useAppState } from "../../../app";
import { ApplicationMessageScreen } from "../../../messages";
import { usePageNavigation } from "../../../pages/use-page-navigation";
import { useGetGeneralCodes } from "../../../services/data-list";
import {
  CreateInterfaceDeploymentRequestRequest,
  UpdateInterfaceDeploymentRequestRequest,
  useCreateInterfaceDeploymentRequest,
  useUpdateInterfaceDeploymentRequest,
} from "../../../services/deployment-request";
import {
  DplymntReqSeq,
  useCreateComponentDeploymentRequests,
  useUpdateComponentDeploymentRequests,
} from "../../../services/deployment-requests";
import { formattedDateTime } from "../../../util/date-util";
import { ENV } from "@vrf/fr-vrf-core";
import {
  DeploymentDivisionType,
  DeploymentRequestScreenProcessingDivisionType,
  DeploymentRequestType,
  EnvTypeCd,
} from "../../../util/general-code/enums/general-codes";
import { Option } from "../../../util/option";
import { createModal } from "../../modal";
import {
  CreateIfList,
  UpdateIfList,
  useDeploymentRequestInfomationInputDialogState,
} from "./";
import {
  DPLY_IMPL_TYPE,
  REQUEST_CD_TYPES,
  createDplymntDtime,
  requestCdTypes,
} from "./DeploymentRequestInfomationInputDialogConstant";

export const DeploymentRequestInfomationInputDialogContent = () => {
  const { showConfirmationDialog, errorMessages, showToast, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const [_appState, setAppState] = useAppState();
  const [dialogState, setDialogState] =
    useDeploymentRequestInfomationInputDialogState();
  const { moveToModalPage, closeModalPage, isModalPage } = usePageNavigation();
  const { fieldPath } = dialogState;
  const [getGeneralCodesResponse, sendGeneralCodesRequest] =
    useGetGeneralCodes();
  const [
    updateInterfaceDeploymentResponse,
    sendUpdateInterfaceDeploymentRequest,
  ] = useUpdateInterfaceDeploymentRequest();
  const [
    createInterfaceDeploymentResponse,
    sendCreateInterfaceDeploymentRequest,
  ] = useCreateInterfaceDeploymentRequest();
  const [
    createComponentDeploymentRequest,
    sendCreateComponentDeploymentRequest,
  ] = useCreateComponentDeploymentRequests();
  const [
    updateComponentDeploymentRequest,
    sendUpdateComponentDeploymentRequest,
  ] = useUpdateComponentDeploymentRequests();

  const navigate = useNavigate();

  const onInitialDisplayed = () => {
    if (dialogState.isShow) {
      moveToModalPage();
    }
    resetUserError();
    sendGeneralCodesRequest({ cdTypes: requestCdTypes });
    setDialogState((prevState) => ({
      ...prevState,
      isSaved: true,
    }));
  };

  const onGetGetGeneralCodesResponse = () => {
    if (!getGeneralCodesResponse) return;
    const GeneralCode = getGeneralCodesResponse.gnrlCdTypeList;
    const deploymentDivisionType: Option[] = GeneralCode.flatMap((e) => {
      if (e.cdType === REQUEST_CD_TYPES.DEPLOYMENT_DIVISION_TYPE)
        return e.cdList;
      return [];
    });

    setDialogState((prevState) => ({
      ...prevState,
      generalCodes: {
        deploymentDivisionType: deploymentDivisionType,
      },
    }));
  };

  const onGetCreateInterfaceDeploymentResponse = () => {
    if (!createInterfaceDeploymentResponse) return;

    if (
      !createInterfaceDeploymentResponse.warningMessage ||
      !createInterfaceDeploymentResponse.warningMessage.length
    ) {
      closeModalPage();
      setDialogState((prevState) => ({
        ...prevState,
        isShow: false,
        isSaved: true,
      }));
      // Display success toast message at transition destination
      setAppState((prevState) => ({ ...prevState, isShowSuccessToast: true }));
      transition(
        `/interface-deployment-request-list?dplymntEnv=${dialogState.processTyp}&pjtId=${dialogState.createifListMap[0].pjtId}`,
      );
      return;
    }

    if (dialogState.processTyp === EnvTypeCd.STG) {
      showConfirmationDialog({
        type: "warning",
        title: "",
        message: createInterfaceDeploymentResponse.warningMessage.join("\n"),
        onOkButtonClicked: () => {
          const ifList: CreateIfList[] = dialogState.createifListMap.flatMap(
            (e) => e.ifList,
          );
          sendCreateInterfaceDeploymentRequest({
            processTyp: dialogState.processTyp,
            dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
            pjtId: dialogState.createifListMap[0].pjtId,
            dplyImplType: dialogState.dplyImplType,
            dplymntDtime:
              dialogState.dplyImplType ===
              DeploymentDivisionType.IMMEDIATELY_DEPLOY
                ? formattedDateTime(new Date())
                : createDplymntDtime(
                    dialogState.dplymntDate,
                    dialogState.dplymntTime,
                  ),
            forceStgDplyFlg: true,
            ifList: ifList,
          });
        },
      });
    }
  };

  const onGetUpdateInterfaceDeploymentResponse = () => {
    if (!updateInterfaceDeploymentResponse) return;

    if (
      !updateInterfaceDeploymentResponse.warningMessage ||
      !updateInterfaceDeploymentResponse.warningMessage.length
    ) {
      closeModalPage();
      setDialogState((prevState) => ({
        ...prevState,
        isShow: false,
        isSaved: true,
      }));
      // Display success toast message at transition destination
      setAppState((prevState) => ({ ...prevState, isShowSuccessToast: true }));
      transition(
        `/interface-deployment-request-list?dplymntEnv=${dialogState.processTyp}&pjtId=${dialogState.updateifListMap[0].pjtId}`,
      );
      return;
    }

    if (dialogState.processTyp === EnvTypeCd.STG) {
      showConfirmationDialog({
        type: "warning",
        title: "",
        message: updateInterfaceDeploymentResponse.warningMessage.join("\n"),
        onOkButtonClicked: () => {
          const ifList: UpdateIfList[] = dialogState.updateifListMap.flatMap(
            (e) => e.ifList,
          );
          sendUpdateInterfaceDeploymentRequest({
            dplymntReqSeq: dialogState.deploymentRequestList[0].dplymntReqSeq,
            dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
            dplyImplType: dialogState.dplyImplType,
            dplymntDtime:
              dialogState.dplyImplType ===
              DeploymentDivisionType.IMMEDIATELY_DEPLOY
                ? formattedDateTime(new Date())
                : createDplymntDtime(
                    dialogState.dplymntDate,
                    dialogState.dplymntTime,
                  ),
            forceStgDplyFlg: true,
            updateDtimeTDplymntReqMng:
              dialogState.deploymentRequestList[0].updateDtimeTDplymntReqMng,
            ifList: ifList,
          });
        },
      });
      return;
    }
  };

  const onGetCreateComponentDeploymentRequest = () => {
    if (!createComponentDeploymentRequest) {
      return;
    }

    if (Array.isArray(createComponentDeploymentRequest)) {
      closeModalPage();
      setDialogState((prevState) => ({
        ...prevState,
        isShow: false,
        isSaved: true,
      }));
      dialogState.onCreateComponentDeploymentRequestError(
        createComponentDeploymentRequest,
      );
      return;
    }
    if (
      !createComponentDeploymentRequest.warningMessage ||
      !createComponentDeploymentRequest.warningMessage.length
    ) {
      closeModalPage();
      setDialogState((prevState) => ({
        ...prevState,
        isShow: false,
        isSaved: true,
      }));
      // Display success toast message at transition destination
      setAppState((prevState) => ({ ...prevState, isShowSuccessToast: true }));
      transition(
        `/component-deployment-request-list?screenMode=${DeploymentRequestScreenProcessingDivisionType.NORMAL_MODE}&environment=${dialogState.processTyp}`,
      );
      return;
    }
    if (dialogState.processTyp === EnvTypeCd.STG) {
      showConfirmationDialog({
        type: "warning",
        title: "",
        message: createComponentDeploymentRequest.warningMessage.join("\n"),
        onOkButtonClicked: () => {
          sendCreateComponentDeploymentRequest({
            processTyp: dialogState.processTyp,
            forceStgDplyFlg: true,
            dplyImplType: dialogState.dplyImplType,
            dplymntDtime:
              dialogState.dplyImplType ===
              DeploymentDivisionType.IMMEDIATELY_DEPLOY
                ? formattedDateTime(new Date())
                : createDplymntDtime(
                    dialogState.dplymntDate,
                    dialogState.dplymntTime,
                  ),
            componentList: dialogState.componentMap,
          });
        },
      });
    }
  };

  const onGetUpdateComponentDeploymentRequest = () => {
    if (!updateComponentDeploymentRequest) return;
    if (
      !updateComponentDeploymentRequest.warningMessage ||
      !updateComponentDeploymentRequest.warningMessage.length
    ) {
      closeModalPage();
      setDialogState((prevState) => ({
        ...prevState,
        isShow: false,
        isSaved: true,
      }));
      // Display success toast message at transition destination
      setAppState((prevState) => ({ ...prevState, isShowSuccessToast: true }));
      transition(
        `/component-deployment-request-list?screenMode=${DeploymentRequestScreenProcessingDivisionType.NORMAL_MODE}&environment=${dialogState.processTyp}`,
      );
      return;
    }

    if (dialogState.processTyp === EnvTypeCd.STG) {
      showConfirmationDialog({
        type: "warning",
        title: "",
        message: updateComponentDeploymentRequest.warningMessage.join("\n"),
        onOkButtonClicked: () => {
          const dplymntReqSeqList: DplymntReqSeq[] =
            dialogState.deploymentRequestList.map((e) => {
              const dplymntReqSeq: DplymntReqSeq = {
                dplymntReqSeq: e.dplymntReqSeq,
                updateDtimeTDplymntReqMng: e.updateDtimeTDplymntReqMng,
              };
              return dplymntReqSeq;
            });
          sendUpdateComponentDeploymentRequest({
            dplymntReqSeqList: dplymntReqSeqList,
            dplyImplType: dialogState.dplyImplType,
            dplymntDtime:
              dialogState.dplyImplType ===
              DeploymentDivisionType.IMMEDIATELY_DEPLOY
                ? undefined
                : createDplymntDtime(
                    dialogState.dplymntDate,
                    dialogState.dplymntTime,
                  ),
            forceStgDplyFlg: true,
          });
        },
      });
      return;
    }
  };

  useEffect(onInitialDisplayed, [dialogState.isShow]);
  useEffect(onGetGetGeneralCodesResponse, [getGeneralCodesResponse]);
  useEffect(onGetCreateInterfaceDeploymentResponse, [
    createInterfaceDeploymentResponse,
  ]);
  useEffect(onGetUpdateInterfaceDeploymentResponse, [
    updateInterfaceDeploymentResponse,
  ]);
  useEffect(onGetCreateComponentDeploymentRequest, [
    createComponentDeploymentRequest,
  ]);
  useEffect(onGetUpdateComponentDeploymentRequest, [
    updateComponentDeploymentRequest,
  ]);

  const transition = (page: string) => {
    const href = createPath({
      pathname: page,
    });
    navigate(href);
  };

  const changeCheckHandle = (event: CustomEvent) => {
    const value = event.detail.value;
    setDialogState((prevState) => ({
      ...prevState,
      dplyImplType: value,
      isSaved: false,
    }));
  };

  const changeDateValueHandle = (event: CustomEvent) => {
    const value = event.detail.date;
    setDialogState((prevState) => ({
      ...prevState,
      dplymntDate: value,
      isSaved: false,
    }));
  };

  const changeTimeValueHandle = (event: CustomEvent) => {
    const value = event.detail.value;
    setDialogState((prevState) => ({
      ...prevState,
      dplymntTime: value,
      isSaved: false,
    }));
  };

  const interfaceDeploy = () => {
    if (dialogState.deploymentRequestList.length === 0) {
      const ifList: CreateIfList[] = dialogState.createifListMap.flatMap(
        (e) => e.ifList,
      );

      const createInterfaceDeploymentRequestData: CreateInterfaceDeploymentRequestRequest =
        {
          processTyp: dialogState.processTyp,
          dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
          pjtId: dialogState.createifListMap[0].pjtId,
          dplyImplType: dialogState.dplyImplType,
          dplymntDtime:
            dialogState.dplyImplType ===
            DeploymentDivisionType.IMMEDIATELY_DEPLOY
              ? formattedDateTime(new Date())
              : createDplymntDtime(
                  dialogState.dplymntDate,
                  dialogState.dplymntTime,
                ),
          forceStgDplyFlg: false,
          ifList: ifList,
        };
      sendCreateInterfaceDeploymentRequest(
        createInterfaceDeploymentRequestData,
      );
    } else if (dialogState.deploymentRequestList.length > 0) {
      const ifList: UpdateIfList[] = dialogState.updateifListMap.flatMap(
        (e) => e.ifList,
      );

      const updateInterfaceDeploymentRequestData: UpdateInterfaceDeploymentRequestRequest =
        {
          dplymntReqSeq: dialogState.deploymentRequestList[0].dplymntReqSeq,
          dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
          dplyImplType: dialogState.dplyImplType,
          dplymntDtime:
            dialogState.dplyImplType ===
            DeploymentDivisionType.IMMEDIATELY_DEPLOY
              ? formattedDateTime(new Date())
              : createDplymntDtime(
                  dialogState.dplymntDate,
                  dialogState.dplymntTime,
                ),
          forceStgDplyFlg: false,
          updateDtimeTDplymntReqMng:
            dialogState.deploymentRequestList[0].updateDtimeTDplymntReqMng,
          ifList: ifList,
        };

      sendUpdateInterfaceDeploymentRequest(
        updateInterfaceDeploymentRequestData,
      );
    }
  };

  const componentDeploy = () => {
    if (dialogState.deploymentRequestList.length === 0) {
      sendCreateComponentDeploymentRequest({
        processTyp: dialogState.processTyp,
        forceStgDplyFlg: false,
        dplyImplType: dialogState.dplyImplType,
        dplymntDtime:
          dialogState.dplyImplType === DeploymentDivisionType.IMMEDIATELY_DEPLOY
            ? formattedDateTime(new Date())
            : createDplymntDtime(
                dialogState.dplymntDate,
                dialogState.dplymntTime,
              ),
        componentList: dialogState.componentMap,
      });
    } else if (dialogState.deploymentRequestList.length !== 0) {
      const dplymntReqSeqList: DplymntReqSeq[] =
        dialogState.deploymentRequestList.map((e) => {
          const dplymntReqSeq: DplymntReqSeq = {
            dplymntReqSeq: e.dplymntReqSeq,
            updateDtimeTDplymntReqMng: e.updateDtimeTDplymntReqMng,
          };
          return dplymntReqSeq;
        });
      sendUpdateComponentDeploymentRequest({
        dplymntReqSeqList: dplymntReqSeqList,
        dplyImplType: dialogState.dplyImplType,
        dplymntDtime:
          dialogState.dplyImplType === DeploymentDivisionType.IMMEDIATELY_DEPLOY
            ? undefined
            : createDplymntDtime(
                dialogState.dplymntDate,
                dialogState.dplymntTime,
              ),
        forceStgDplyFlg: false,
      });
    }
  };

  const clickedDeployConfirmDialogOKButton = () => {
    resetUserError();
    if (dialogState.dplymntReqType === DeploymentRequestType.INTERFACE) {
      interfaceDeploy();
    } else if (dialogState.dplymntReqType === DeploymentRequestType.COMPONENT) {
      componentDeploy();
    }
  };

  const clickedSubmitDeployRequest = (_event: CustomEvent) => {
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
      onOkButtonClicked: clickedDeployConfirmDialogOKButton,
    });
  };

  const onClickCancel = () => {
    if (dialogState.isSaved) {
      closeModalPage();
      setDialogState((prevState) => ({
        ...prevState,
        isShow: false,
        isSaved: true,
      }));
      return;
    }
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
      onOkButtonClicked: () => {
        closeModalPage();
        setDialogState((prevState) => ({
          ...prevState,
          isShow: false,
          isSaved: true,
        }));
      },
    });
  };

  return (
    <>
      {createModal(
        "Input Deployment Request Information",
        <>
          <FrVrfRadioButtonField
            labelContent="Deployment Division"
            optionItems={dialogState.generalCodes.deploymentDivisionType}
            style={{
              "--vrf-radio-button-field-label-width": "80px",
            }}
            checked={dialogState.dplyImplType}
            onRadioCheckedChanged={changeCheckHandle}
            errorMessage={errorMessages(fieldPath.dplyImplType, isModalPage())}
          />
          <br />
          <FrVrfInputDatepickerField
            labelContent="Scheduled Deployment Date Time"
            defaultDate={dialogState.dplymntDate}
            dateFormat="ymd"
            placeholder="Please input..."
            clearButtonVisible
            disabled={
              dialogState.dplyImplType === DPLY_IMPL_TYPE.IMMEDIATELY_DEPLOY
            }
            style={{
              "--vrf-input-datepicker-field-label-width": "200px",
              "--vrf-input-datepicker-field-input-width": "150px",
              padding: "10px",
            }}
            onInputValueChanged={changeDateValueHandle}
            errorMessage={errorMessages(fieldPath.dplymntDate, isModalPage())}
          />
          <FrVrfTimeInputField
            defaultTime={dialogState.dplymntTime}
            placeholder="Please input..."
            clearButtonVisible
            disabled={
              // visible is more appropriate, but onValueChanged doesn't work somehow if visible is used.
              dialogState.dplyImplType === DPLY_IMPL_TYPE.IMMEDIATELY_DEPLOY
            }
            style={{
              "--vrf-time-input-field-select-width": "150",
            }}
            onValueChanged={changeTimeValueHandle}
            errorMessage={errorMessages(fieldPath.dplymntTime, isModalPage())}
          />
          <FrVrfSingleLineDisplayField
            label="Time Zone : "
            firstValue={Intl.DateTimeFormat().resolvedOptions().timeZone}
          />
          <FrVrfFooter>
            <>
              <FrVrfButton
                style={{ marginLeft: "auto" }}
                content="Cancel"
                theme="secondary"
                size="large"
                onClicked={onClickCancel}
              />
              <FrVrfButton
                content="Submit Deploy Request"
                theme="primary"
                size="large"
                onClicked={clickedSubmitDeployRequest}
              />
            </>
          </FrVrfFooter>
        </>,
        () => {},
        "small",
        undefined,
        dialogState.processTyp,
      )}
    </>
  );
};
